export type CourseCountry = string;

export interface HermesCourse {
  id: number;
  name: string;
  level_of_study: string | null;
  level_display: string | null;
  duration: string | null;
  areas_of_study: string | null;
  provider_name: string;
  country: CourseCountry;
  website: string | null;
  course_url: string | null;
  description: string | null;
  created_at?: string;
}

export interface CountryItem {
  code: string;
  name: string;
}

export interface ProviderItem {
  id?: number;
  name: string;
  country_code: string;
  course_count: number;
}

export type ProviderSummary = ProviderItem;

export interface StudyLevelItem {
  id?: number;
  country_code?: string;
  raw_value: string;
  display_name: string;
  rank?: number;
  course_count?: number;
}

export interface CourseCatalog {
  countries: CountryItem[];
  providers: ProviderItem[];
  studyLevels: StudyLevelItem[];
}

export interface CourseSearchParams {
  country?: string;
  search?: string;
  provider?: string;
  level?: string; // raw_value
  page?: number;
  pageSize?: number;
}

export interface CourseSearchResult {
  courses: HermesCourse[];
  total: number;
  page: number;
  pageSize: number;
}

const PUBLIC_API_KEY = import.meta.env.VITE_COURSES_API_KEY
  || 'sb_publishable_ACJWlzQHlZjBrEguHvfOxg_3BJgxAaH';
const API_BASE = import.meta.env.VITE_COURSES_API_URL || '/api';
const PAGE_SIZE = 20;

const request = async <T>(
  endpoint: string,
  params?: URLSearchParams,
  options: RequestInit = {}
): Promise<T> => {
  const queryString = params ? `?${params.toString()}` : '';
  const url = `${API_BASE}${endpoint}${queryString}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      apikey: PUBLIC_API_KEY,
      Authorization: `Bearer ${PUBLIC_API_KEY}`,
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`API request failed (${response.status}): ${detail}`);
  }

  return response.json() as Promise<T>;
};

export const countryName = (code: string) => {
  if (!code) return '';
  const upper = code.trim().toUpperCase();
  const MAP: Record<string, string> = {
    UK: 'United Kingdom',
    USA: 'United States',
    US: 'United States',
    IE: 'Ireland',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
  };
  return MAP[upper] || code;
};

export const formatStudyLevel = (course: Pick<HermesCourse, 'level_display' | 'level_of_study'> | string | null) => {
  if (!course) return '';
  if (typeof course === 'object') {
    return course.level_display || course.level_of_study || '';
  }
  return course;
};

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 90);

export const coursePath = (course: Pick<HermesCourse, 'id' | 'name' | 'country' | 'course_url'>) => {
  if (course.course_url?.startsWith('/courses/')) return course.course_url;
  return `/courses/${course.country.toLowerCase()}/${course.id}-${slugify(course.name)}`;
};

export const providerCoursesPath = (provider: { name: string; country?: string; country_code?: string }) => {
  const c = provider.country || provider.country_code || '';
  const params = new URLSearchParams();
  if (c) params.set('country', c);
  params.set('provider', provider.name);
  return `/courses?${params.toString()}`;
};

export const searchCourses = async ({
  country = '',
  search = '',
  provider = '',
  level = '',
  page = 1,
  pageSize = PAGE_SIZE,
}: CourseSearchParams = {}): Promise<CourseSearchResult> => {
  const offset = (page - 1) * pageSize;
  const params = new URLSearchParams({
    select: 'id,name,level_of_study,level_display,duration,areas_of_study,provider_name,country,website,course_url',
    limit: String(pageSize),
    offset: String(offset),
    order: 'provider_name.asc,name.asc,id.asc',
  });

  if (country) params.set('country', `eq.${country}`);
  if (provider) params.set('provider_name', `eq.${provider}`);
  if (level) {
    params.set('level_of_study', `eq.${level}`);
  }

  const cleanSearch = search.trim().replace(/[,*().]/g, ' ').replace(/\s+/g, ' ').slice(0, 100);
  if (cleanSearch) {
    params.set(
      'or',
      `(name.ilike.*${cleanSearch}*,provider_name.ilike.*${cleanSearch}*,areas_of_study.ilike.*${cleanSearch}*)`,
    );
  }

  const courses = await request<HermesCourse[]>('/courses', params);
  const total = courses.length === pageSize ? offset + pageSize + 1 : offset + courses.length;

  return { courses, total, page, pageSize };
};

export const getCourseById = async (id: number) => {
  const params = new URLSearchParams({
    select: '*',
    id: `eq.${id}`,
    limit: '1',
  });
  const data = await request<HermesCourse[]>('/courses', params);
  return data[0] || null;
};

export const getRelatedCourses = async (course: HermesCourse, limit = 4) => {
  const params = new URLSearchParams({
    select: 'id,name,level_of_study,level_display,duration,areas_of_study,provider_name,country,website,course_url',
    provider_name: `eq.${course.provider_name}`,
    id: `neq.${course.id}`,
    limit: String(limit),
    order: 'name.asc',
  });
  return request<HermesCourse[]>('/courses', params);
};

export const getCountryLevels = async (country: string): Promise<StudyLevelItem[]> => {
  try {
    return await request<StudyLevelItem[]>('/rpc/get_country_levels', undefined, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_country: country }),
    });
  } catch (e) {
    console.error("Failed to fetch country levels via RPC:", e);
    return [];
  }
};

export const getProviderLevels = async (provider: string, country?: string): Promise<StudyLevelItem[]> => {
  try {
    return await request<StudyLevelItem[]>('/rpc/get_provider_levels', undefined, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_provider: provider, p_country: country || null }),
    });
  } catch (e) {
    console.error("Failed to fetch provider levels via RPC:", e);
    return [];
  }
};

let catalogPromise: Promise<CourseCatalog> | null = null;

export const getCourseCatalog = (): Promise<CourseCatalog> => {
  if (catalogPromise) return catalogPromise;

  catalogPromise = Promise.all([
    request<CountryItem[]>('/countries', new URLSearchParams({ select: 'code,name', order: 'name.asc' })),
    request<ProviderItem[]>('/providers', new URLSearchParams({ select: 'id,name,country_code,course_count', order: 'course_count.desc' })),
    request<StudyLevelItem[]>('/v_levels', new URLSearchParams({ select: 'country_code,raw_value,display_name,rank', order: 'rank.asc' })),
  ])
    .then(([countries, providers, studyLevels]) => ({
      countries,
      providers,
      studyLevels,
    }))
    .catch((err) => {
      catalogPromise = null;
      throw err;
    });

  return catalogPromise;
};